import { getModulesPermissionAsPerUser } from 'app/api/onboarding';
import PageLoader from 'app/components/PageLoader';
import { APP_CONSTANTS, LocalSessionKey } from 'app/constants/app-constants';
import Message from 'app/utils/Message';
import { LocalStorage } from 'app/utils/storage';
import Toast from 'app/utils/Toast';
import { useCallback, useEffect, useMemo, useState } from 'react';
import Header from '../header';
import Sidebar from '../Sidebar';
import './index.scss';
import { useNavigate } from 'react-router-dom';

const BasePage = ({ children }) => {
  const navigate = useNavigate();
  const [mallList, setMallList] = useState([]);
  const [selectedMallIds, setSelectedMallIds] = useState('');
  const [moduleList, setModuleList] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [localStorageUpdated, setLocalStorageUpdated] = useState(false);

  const userMallListResponse = useMemo(() => {
    return JSON.parse(LocalStorage.get(LocalSessionKey.MALL_LIST));
  }, [localStorageUpdated]);

  const getModulesPermissionAsPerUserAPI = useCallback(
    async (mallIdsToSend) => {
      setIsLoading(true);
      try {
        const response = await getModulesPermissionAsPerUser(mallIdsToSend);
        if (response?.responseCode === 200) {
          const moduleList = response?.data?.permission.map(
            (mall) => mall?.name
          );
          setModuleList(moduleList);
          LocalStorage.set(
            LocalSessionKey.MODULE_LIST,
            JSON.stringify(moduleList)
          );
          setLocalStorageUpdated((prev) => !prev); // Trigger re-render
          navigate(`${APP_CONSTANTS.Profile}`);
        } else {
          Toast.error(
            response?.response?.message
              ? response?.response?.message
              : Message.Customer.STATUS_UPDATED_FAILURE
          );
        }
      } catch (error) {
        Toast.error('An error occurred while fetching permissions.');
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    if (userMallListResponse && mallList.length === 0) {
      const malls = userMallListResponse?.mall_List?.map((mall) => ({
        value: mall.id,
        label: mall.name,
      }));
      const valuesArray = userMallListResponse?.mallId.map((mall) => mall);
      const joinedString = valuesArray.join(',');
      const allMallsOption = {
        value: joinedString,
        label: 'All Malls',
      };
      setMallList([allMallsOption, ...malls]);
      const mallIds = LocalStorage.get(LocalSessionKey.MALL_ID)
        ? LocalStorage.get(LocalSessionKey.MALL_ID)
        : joinedString;
      const moduleList = JSON.parse(
        LocalStorage.get(LocalSessionKey.MODULE_LIST)
      );

      if (moduleList) {
        setModuleList(moduleList);
      }
      setSelectedMallIds(mallIds);
      setIsLoading(false);
    }
  }, [userMallListResponse, mallList.length, localStorageUpdated]);

  useEffect(() => {
    const mallIds = LocalStorage.get(LocalSessionKey.MALL_ID);
    if (selectedMallIds && selectedMallIds !== mallIds) {
      const mallIdsToSend = selectedMallIds;
      if (!LocalStorage.get(LocalSessionKey.MODULE_LIST) || !mallIdsToSend) {
        if (mallIdsToSend) {
          LocalStorage.set(LocalSessionKey.MALL_ID, mallIdsToSend);
          setLocalStorageUpdated((prev) => !prev); // Trigger re-render
          getModulesPermissionAsPerUserAPI(mallIdsToSend);
        }
      }
    }
  }, [selectedMallIds, userMallListResponse, getModulesPermissionAsPerUserAPI]);

  const handleChange = useCallback(
    async (selectedValues) => {
      const { value } = selectedValues.target;
      setSelectedMallIds(value);
      LocalStorage.set(LocalSessionKey.MALL_ID, value);
      setLocalStorageUpdated((prev) => !prev); // Trigger re-render
      await getModulesPermissionAsPerUserAPI(value);
    },
    [mallList, getModulesPermissionAsPerUserAPI]
  );

  return (
    <div>
      {isLoading && <PageLoader isLoading={isLoading} />}
      <Header
        mallList={mallList}
        selectedMallIds={selectedMallIds}
        handleChange={handleChange}
      />
      <div className='flexWrapper'>
        <Sidebar initialsidebar={moduleList} isLoading={isLoading} />
        <div className='contentWrapper' key={isLoading ? 'loading' : 'loaded'}>
          {isLoading ? '' : children}
        </div>
      </div>
    </div>
  );
};

export default BasePage;
